import React from 'react'
import {
  useEditor,
  EditorContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import Image from '@tiptap/extension-image'
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Link2,
  Link2Off,
  ImagePlus,
  Paperclip,
  Undo2,
  Redo2,
  Type,
} from 'lucide-react'

const ResizableImage = (props: any) => {
  const { node, selected, updateAttributes } = props
  const { src, alt, width, height } = node.attrs || {}
  const imgRef = React.useRef<HTMLImageElement | null>(null)
  const [size, setSize] = React.useState<{ width?: number; height?: number }>({})
  const sizeRef = React.useRef<{ width?: number; height?: number }>({})
  const resizeState = React.useRef<{
    startX: number
    startY: number
    startW: number
    startH: number
    ratio: number
  } | null>(null)

  React.useEffect(() => {
    const nextWidth = typeof width === 'number' ? width : Number(width)
    const nextHeight = typeof height === 'number' ? height : Number(height)
    const nextSize = {
      width: Number.isFinite(nextWidth) ? nextWidth : undefined,
      height: Number.isFinite(nextHeight) ? nextHeight : undefined,
    }
    sizeRef.current = nextSize
    setSize(nextSize)
  }, [width, height])

  const onPointerMove = React.useCallback(
    (event: PointerEvent) => {
      if (!resizeState.current) return
      const { startX, startY, startW, startH, ratio } = resizeState.current
      const deltaX = event.clientX - startX
      const deltaY = event.clientY - startY
      const delta = Math.max(deltaX, deltaY)
      let nextWidth = Math.max(120, startW + delta)
      let nextHeight = Math.max(80, startH)
      if (ratio && Number.isFinite(ratio)) {
        nextHeight = Math.round(nextWidth / ratio)
      }
      const nextSize = { width: nextWidth, height: nextHeight }
      sizeRef.current = nextSize
      setSize(nextSize)
    },
    []
  )

  const onPointerUp = React.useCallback(() => {
    if (!resizeState.current) return
    const nextWidth = Math.round(sizeRef.current.width || resizeState.current.startW)
    const nextHeight = Math.round(sizeRef.current.height || resizeState.current.startH)
    resizeState.current = null
    updateAttributes({
      width: nextWidth,
      height: nextHeight,
    })
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
  }, [onPointerMove, updateAttributes])

  React.useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
    }
  }, [onPointerMove, onPointerUp])

  const onResizeStart = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    const rect = imgRef.current?.getBoundingClientRect()
    const startW = rect?.width || 320
    const startH = rect?.height || 200
    const ratio = startW / startH || 1
    resizeState.current = {
      startX: event.clientX,
      startY: event.clientY,
      startW,
      startH,
      ratio,
    }
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
  }

  return (
    <NodeViewWrapper className={`notes-image-wrapper${selected ? ' selected' : ''}`} contentEditable={false}>
      <img
        ref={imgRef}
        src={src}
        alt={alt || 'image'}
        draggable={false}
        style={{
          width: size.width ? `${size.width}px` : 'auto',
          height: size.height ? `${size.height}px` : 'auto',
          maxWidth: '100%',
        }}
      />
      <button
        type="button"
        className="notes-image-handle"
        onPointerDown={onResizeStart}
        aria-label="Resize image"
      />
    </NodeViewWrapper>
  )
}

const ImageWithKey = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      'data-key': { default: null },
      alt: { default: null },
      width: {
        default: null,
        parseHTML: (element) => {
          const value = element.getAttribute('width')
          return value ? Number(value) : null
        },
      },
      height: {
        default: null,
        parseHTML: (element) => {
          const value = element.getAttribute('height')
          return value ? Number(value) : null
        },
      },
    }
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImage)
  },
})

type ImageInsert = { url: string; key: string; name?: string }

type NotesRichEditorProps = {
  value: string
  placeholder?: string
  onChange: (html: string) => void
  showUploadActions?: boolean
  onImageUpload?: (file: File) => Promise<ImageInsert | null>
  onAttachmentUpload?: (file: File) => Promise<void>
}

export default function NotesRichEditor({
  value,
  placeholder = 'Write booking notes…',
  onChange,
  showUploadActions = true,
  onImageUpload = async () => null,
  onAttachmentUpload = async () => {},
}: NotesRichEditorProps) {
  const imageInputRef = React.useRef<HTMLInputElement | null>(null)
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      Link.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noreferrer',
        },
      }),
      ImageWithKey,
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    editorProps: {
      attributes: {
        class: 'notes-editor-content',
      },
    },
  })

  React.useEffect(() => {
    if (!editor) return
    const current = editor.getHTML()
    if (current !== (value || '')) {
      editor.commands.setContent(value || '', false)
    }
  }, [editor, value])

  const setLink = () => {
    if (!editor) return
    const prev = editor.getAttributes('link')?.href || ''
    const url = window.prompt('Paste a link', prev || 'https://')
    if (url === null) return
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run()
  }

  const handleImageFile = async (file?: File | null) => {
    if (!file || !editor) return
    const uploaded = await onImageUpload(file)
    if (!uploaded) return
    editor
      .chain()
      .focus()
      .setImage({
        src: uploaded.url,
        'data-key': uploaded.key,
        alt: uploaded.name || file.name || 'image',
      } as any)
      .run()
  }

  const handleFileUpload = async (file?: File | null) => {
    if (!file) return
    await onAttachmentUpload(file)
  }

  const headingValue = editor?.isActive('heading', { level: 2 })
    ? 'h2'
    : editor?.isActive('heading', { level: 3 })
      ? 'h3'
      : 'p'

  return (
    <div className="notes-editor-shell">
      <div className="notes-toolbar">
        <div className="notes-toolbar-group">
          <Type size={16} />
          <select
            className="notes-toolbar-select"
            value={headingValue}
            onChange={(e) => {
              if (!editor) return
              const next = e.target.value
              if (next === 'p') editor.chain().focus().setParagraph().run()
              if (next === 'h2') editor.chain().focus().toggleHeading({ level: 2 }).run()
              if (next === 'h3') editor.chain().focus().toggleHeading({ level: 3 }).run()
            }}
          >
            <option value="p">Body</option>
            <option value="h2">Heading</option>
            <option value="h3">Subheading</option>
          </select>
        </div>

        <div className="notes-toolbar-group">
          <button
            type="button"
            className={`notes-btn ${editor?.isActive('bold') ? 'active' : ''}`}
            onClick={() => editor?.chain().focus().toggleBold().run()}
            aria-label="Bold"
          >
            <Bold size={16} />
          </button>
          <button
            type="button"
            className={`notes-btn ${editor?.isActive('italic') ? 'active' : ''}`}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            aria-label="Italic"
          >
            <Italic size={16} />
          </button>
          <button
            type="button"
            className={`notes-btn ${editor?.isActive('underline') ? 'active' : ''}`}
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            aria-label="Underline"
          >
            <UnderlineIcon size={16} />
          </button>
          <button
            type="button"
            className={`notes-btn ${editor?.isActive('strike') ? 'active' : ''}`}
            onClick={() => editor?.chain().focus().toggleStrike().run()}
            aria-label="Strikethrough"
          >
            <Strikethrough size={16} />
          </button>
        </div>

        <div className="notes-toolbar-group">
          <button
            type="button"
            className={`notes-btn ${editor?.isActive('bulletList') ? 'active' : ''}`}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            aria-label="Bullet list"
          >
            <List size={16} />
          </button>
          <button
            type="button"
            className={`notes-btn ${editor?.isActive('orderedList') ? 'active' : ''}`}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            aria-label="Numbered list"
          >
            <ListOrdered size={16} />
          </button>
          <button
            type="button"
            className={`notes-btn ${editor?.isActive('blockquote') ? 'active' : ''}`}
            onClick={() => editor?.chain().focus().toggleBlockquote().run()}
            aria-label="Quote"
          >
            <Quote size={16} />
          </button>
        </div>

        <div className="notes-toolbar-group">
          <button type="button" className="notes-btn" onClick={setLink} aria-label="Insert link">
            <Link2 size={16} />
          </button>
          <button
            type="button"
            className="notes-btn"
            onClick={() => editor?.chain().focus().unsetLink().run()}
            aria-label="Remove link"
          >
            <Link2Off size={16} />
          </button>
        </div>

        {showUploadActions && (
          <div className="notes-toolbar-group">
            <button type="button" className="notes-btn" onClick={() => imageInputRef.current?.click()} aria-label="Insert image">
              <ImagePlus size={16} />
            </button>
            <button type="button" className="notes-btn" onClick={() => fileInputRef.current?.click()} aria-label="Upload attachment">
              <Paperclip size={16} />
            </button>
          </div>
        )}

        <div className="notes-toolbar-group">
          <button type="button" className="notes-btn" onClick={() => editor?.chain().focus().undo().run()} aria-label="Undo">
            <Undo2 size={16} />
          </button>
          <button type="button" className="notes-btn" onClick={() => editor?.chain().focus().redo().run()} aria-label="Redo">
            <Redo2 size={16} />
          </button>
        </div>
      </div>

      <EditorContent editor={editor} className="notes-editor" />

      {showUploadActions && (
        <>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0]
              void handleImageFile(file)
              e.currentTarget.value = ''
            }}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0]
              void handleFileUpload(file)
              e.currentTarget.value = ''
            }}
          />
        </>
      )}
    </div>
  )
}
